import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import {
  asPublishedLegalHtml,
  type PublishedLegalHtml,
} from '@/components/legal/LegalHtml'

export type LegalDocumentKind = 'privacy' | 'terms'

export type PublicLegalDocument =
  | { kind: LegalDocumentKind; published: false }
  | {
      kind: LegalDocumentKind
      published: true
      title: string
      effectiveAt: string
      publishedAt: string
      sanitizedHtml: PublishedLegalHtml
      latestChangeNote: string
    }

type PublicLegalDocumentApi =
  | { kind: LegalDocumentKind; published: false }
  | {
      kind: LegalDocumentKind
      published: true
      title?: string
      effectiveAt?: string
      publishedAt?: string
      sanitizedHtml?: string
      latestChangeNote?: string
    }

function mapPublicLegalDocument(
  kind: LegalDocumentKind,
  response: PublicLegalDocumentApi,
): PublicLegalDocument {
  if (
    !response.published ||
    response.kind !== kind ||
    !response.title?.trim() ||
    !response.effectiveAt ||
    !response.publishedAt ||
    !response.sanitizedHtml?.trim() ||
    !response.latestChangeNote?.trim()
  ) {
    return { kind, published: false }
  }

  return {
    kind,
    published: true,
    title: response.title,
    effectiveAt: response.effectiveAt,
    publishedAt: response.publishedAt,
    sanitizedHtml: asPublishedLegalHtml(response.sanitizedHtml),
    latestChangeNote: response.latestChangeNote,
  }
}

export function useLegalDocument(kind: LegalDocumentKind) {
  return useQuery({
    queryKey: ['legal-document', kind],
    queryFn: async () =>
      mapPublicLegalDocument(kind, await api.get<PublicLegalDocumentApi>(`/legal/${kind}`)),
    retry: false,
  })
}
